import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import SearchSelect from './SearchSelect';
import { Field, InfoTip, inputCls, lockedInputCls, tooltipPopupCls } from './FormKit';
import { formatDate } from '../../utils/dateFormat';
import { FyeField } from './FyeField';
// Estimated Fees 的幣別清單——與 QC 表單 money 題同一份（單一出處）。
import { CURRENCIES, DEFAULT_CURRENCY } from '../../pages/qcQuestions';
import { linkSplitToggle, splitListTotal, splitList, splitListStackedRow, splitListTitle, splitListExtraRow, splitListExtraTag } from '../workflow/constants';
import {
  PROFILE_SPLIT_GROUPS, PIE_TYPE_OPTIONS, PIE_TYPE_OTHERS, PIE_LABEL, PIE_TYPE_LABEL, PIE_TYPE_OTHER_LABEL, pieText,
} from '../../utils/engagementProfile';
import SplitList from '../workflow/SplitList';
import {
  lineFeeKey, lineRowKey, ownRowKey, repeatedSlotClashes,
} from '../../utils/serviceLines';
import { descriptionNoticeOf, NoticeLine, rowsFor } from './EngagementCompanies';

const fmtFeeTotal = (v) => {
  const n = Number(v);
  return v === undefined || v === null || v === '' || !Number.isFinite(n)
    ? '—'
    : n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/**
 * The engagement field set — ONE definition for the three surfaces that write
 * an engagement:
 *
 *   Management → Engagements → New / Edit   (/qm/engagements/new, Edit modal)
 *   WORKFLOW → Engagement Registration      (New page + edit page)
 *   WORKFLOW → Engagement Amendment         (New page + Modify modal)
 *
 * Do NOT give any surface its own copy of these controls: per-surface copies
 * drift (pickers, EQR selects, lead lists), and the three must keep asking
 * the same questions.
 *
 * **Engagement Registration is the reference** (it is the newest and the one
 * the firm actually starts projects from), so this module speaks its shape:
 *   - leads are staff-id LISTS (partnerStaffIds / directorStaffIds / managerStaffIds)
 *   - yes/no answers are the strings 'yes' | 'no' | '' (not tri-state booleans)
 *   - the service is (service, serviceOther) — 'Other' carries typed text
 *
 * Conversion to/from the engagement master's shape happens at the two
 * boundaries below (engagementToFields / fieldsToEngagementValues), so each
 * surface keeps its own API contract while sharing the controls and the labels.
 *
 * The per-surface part is only WHICH RECORD the fields belong to — Registration
 * ticks several companies of a group, Management picks one entity, Amendment
 * targets an existing engagement. That stays in each page; everything below the
 * subject picker is shared.
 */

// ── Options shape ──────────────────────────────────────────────────────────
// { partners, directors, managers, staff } = [{ id, name, position }] in
// designation-seniority order (the server sorts by the staff_designation
// lookup) — the pickers keep that order, with no designation section
// headers, and each option shows the bare name (staffOption below).
// { services } = string[]
// Served identically by /api/workflow/engreg/options, /api/workflow/ear/options
// and /api/engagements/form-options.

// Chips and read surfaces still spell out the designation inline.
export const staffLabel = (s) => `${s.name}${s.position ? ` (${s.position})` : ''}`;

// Person pickers list bare names, no section headers, in the feed's
// grade → designation seniority order.
const staffOption = (o) => ({ value: String(o.id), label: o.name });

// id → { name, position } from any mix of feed rows ({ id, name, position })
// and stored people ({ staffId, staffName }). Chips of people no longer in the
// field's own list (promoted to another grade, left the firm) still read by
// name: the feed rows win, the stored name is the fallback.
const staffById = (...lists) => {
  const m = new Map();
  lists.flat().forEach((o) => {
    if (!o) return;
    const id = o.id != null ? o.id : o.staffId;
    if (id == null || m.has(id)) return;
    m.set(id, o.name ? { name: o.name, position: o.position || null } : { name: o.staffName, position: o.position || null });
  });
  return m;
};

export const CONSOLIDATED_FS_OPTIONS = [['yes', 'Yes'], ['no', 'No']];

// ── Controls ───────────────────────────────────────────────────────────────

/** Multi-select staff picker: SearchSelect to add + removable chips.
 *  `lockedIds` chips carry no remove button and a `lockedNote` tag (the
 *  Registration requester, who always stays on the team). */
export const MultiStaffPicker = ({ options = [], selectedIds = [], onChange, placeholder, lockedIds = [], lockedNote = null, known = [] }) => {
  const byId = useMemo(() => staffById(options || [], known || []), [options, known]);
  const add = (id) => {
    const sid = parseInt(id, 10);
    if (!isNaN(sid) && !selectedIds.includes(sid)) onChange([...selectedIds, sid]);
  };
  return (
    <div className="space-y-2">
      <SearchSelect
        value=""
        onChange={add}
        options={(options || [])
          .filter((o) => !selectedIds.includes(o.id))
          .map(staffOption)}
        placeholder={placeholder}
      />
      {selectedIds.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selectedIds.map((sid) => (
            <span key={sid} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-xs">
              {byId.has(sid) ? staffLabel(byId.get(sid)) : `#${sid}`}
              {lockedIds.includes(sid) ? (
                lockedNote && <span className="text-blue-500">· {lockedNote}</span>
              ) : (
                <button type="button" onClick={() => onChange(selectedIds.filter((x) => x !== sid))} className="hover:text-blue-900">
                  <X className="w-3 h-3" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Single-pick staff field that still speaks the id-LIST shape
 * (the Partner in charge is one person). Keeping the list keeps
 * engagement_leads, the API contract and the history diff labels unchanged —
 * only the UI narrows.
 */
export const SingleStaffPicker = ({ options = [], selectedIds = [], onChange, placeholder, known = [] }) => {
  const current = (selectedIds || []).length > 0 ? selectedIds[0] : null;
  // The person already in the field stays selectable (and readable) even when
  // they are no longer in the field's list.
  const extra = current != null && !(options || []).some((o) => o.id === current)
    ? staffById(known || []).get(current) : null;
  return (
    <SearchSelect
      value={current != null ? String(current) : ''}
      onChange={(v) => onChange(v === '' || v == null ? [] : [parseInt(v, 10)])}
      options={[
        ...(options || []).map(staffOption),
        ...(extra ? [{ value: String(current), label: extra.name }] : []),
      ]}
      placeholder={placeholder}
    />
  );
};

/** yes / no radios (plus any extra choice), the Registration answer shape. */
export const YesNo = ({ value, onChange, choices = [['yes', 'Yes'], ['no', 'No']] }) => (
  <div className="flex flex-wrap gap-4">
    {choices.map(([v, label]) => (
      <label key={v} className="flex items-center gap-2 text-sm text-slate-700">
        <input type="radio" checked={value === v} onChange={() => onChange(v)} />
        {label}
      </label>
    ))}
  </div>
);

// ── Shape + conversions ────────────────────────────────────────────────────

export const emptyEngagementFields = () => ({
  yearEnd: '',
  service: '', serviceOther: '',
  descriptionOfService: '', ultimateParent: '',
  overseasOperations: '', consolidatedFs: '',
  // Public Interest Entity (PIE)? — 'yes' / 'no'; the type (and its Others
  // text) only with a Yes. The same two questions the QC6 / QC7 form asks.
  isPie: '', pieType: '', pieTypeOther: '',
  proposedFees: '', proposedFeesCcy: DEFAULT_CURRENCY, ssicPrimary: '', ssicSecondary: '',
  // Registration / Management New with several companies: each company's own
  // amount { <entityId>: amount } — every company, the Total is their sum.
  proposedFeesSplit: {},
  // Same surfaces: the answers a company gives on its own, by form key
  // { <key>: { <entityId>: value } } (see "Per-company answers" below).
  profileSplit: {},
  // Same surfaces: the additional services — one more engagement each
  // [{ k, entityId, service, serviceOther, descriptionOfService }]; the fees
  // sit in proposedFeesSplit under lineFeeKey(k) (src/utils/serviceLines.js).
  serviceLines: [],
  principalActivity1: '', principalActivity2: '',
  secRegistered: '', isGroupAudit: '', underInvestigation: '', tcwgFeeCommunicated: '',
  screeningRequired: '',
  partnerStaffIds: [], directorStaffIds: [], managerStaffIds: [],
  teamStaffIds: [],
  remarks: '',
});

/** The service an engagement carries for the fields' answer (Other → the typed text). */
export const effectiveServiceOf = (f) => (f.service === 'Other' ? String(f.serviceOther || '').trim() : String(f.service || '').trim());

/**
 * Does the chosen service allow several engagements per company and FYE
 * (Dropdown Lists → Service Provided, "Multiple per FYE")? From the second
 * engagement on, each is numbered automatically and carries a Description of
 * Service of its own — the number says nothing, the description is what
 * tells them apart on every list. `multiFyeServices` travels in every form's
 * options (name preview).
 */
export const isMultiFyeService = (f, multiFyeServices = []) => {
  const s = effectiveServiceOf(f).toLowerCase();
  return !!s && (multiFyeServices || []).some((v) => String(v).toLowerCase() === s);
};

// A company already holds an engagement of this service for the FYE (the
// live duplicate-check said so): this one needs a description of its own —
// so the field turns mandatory on the spot.
export const TAKEN_SLOT_DESCRIPTION_TIP = 'This company already has an engagement of this service for the FYE. Begin with a short subject that tells them apart, e.g. "IFRS reporting" beside "SFRS statutory audit", then describe the scope.';

const triToStr = (v) => (v === true ? 'yes' : v === false ? 'no' : '');
const strToTri = (v) => (v === 'yes' ? true : v === 'no' ? false : null);
const blankToNull = (v) => {
  const s = v === null || v === undefined ? '' : String(v).trim();
  return s === '' ? null : s;
};

/**
 * Engagement master record → form fields. `services` decides whether the stored
 * service text is one of the managed options or the free text a Registration
 * 'Other' answer collapsed into (see effectiveService() on the server).
 */
export const engagementToFields = (e, { services = [], partners = [] } = {}) => {
  if (!e) return emptyEngagementFields();
  const stored = e.serviceProvided || '';
  const isListed = stored === '' || (services || []).includes(stored);
  return {
    ...emptyEngagementFields(),
    yearEnd: e.yearEnd ? String(e.yearEnd).slice(0, 10) : '',
    service: isListed ? stored : 'Other',
    serviceOther: isListed ? '' : stored,
    descriptionOfService: e.descriptionOfService || '',
    ultimateParent: e.ultimateParent || '',
    overseasOperations: triToStr(e.overseasOperations),
    consolidatedFs: e.consolidatedFs || '',
    isPie: triToStr(e.isPie),
    pieType: e.isPie === true ? (e.pieType || '') : '',
    pieTypeOther: e.isPie === true ? (e.pieTypeOther || '') : '',
    proposedFees: e.proposedFees == null ? '' : String(e.proposedFees),
    proposedFeesCcy: e.proposedFeesCcy || DEFAULT_CURRENCY,
    ssicPrimary: e.ssicPrimary || '',
    ssicSecondary: e.ssicSecondary || '',
    principalActivity1: e.principalActivity1 || '',
    principalActivity2: e.principalActivity2 || '',
    secRegistered: triToStr(e.secRegistered),
    isGroupAudit: triToStr(e.isGroupAudit),
    underInvestigation: triToStr(e.underInvestigation),
    tcwgFeeCommunicated: triToStr(e.tcwgFeeCommunicated),
    screeningRequired: triToStr(e.screeningRequired),
    partnerStaffIds: (e.partners || []).map((m) => m.staffId).filter((x) => x != null),
    directorStaffIds: (e.directors || []).map((m) => m.staffId).filter((x) => x != null),
    managerStaffIds: (e.managers || []).map((m) => m.staffId).filter((x) => x != null),
    teamStaffIds: (e.teamMembers || []).map((m) => m.staffId).filter((x) => x != null),
    // The people already on the record, so their chips read by name even
    // when they no longer sit in that field's list. Not posted.
    knownStaff: [...(e.partners || []), ...(e.directors || []), ...(e.managers || []), ...(e.teamMembers || [])],
    remarks: e.remarks || '',
  };
};

/**
 * Form fields → the engagement master's value shape. Used BOTH as the
 * Management POST/PATCH body and as the "after" side of the Amendment diff, so
 * the two paths can never disagree about what a field means.
 */
export const fieldsToEngagementValues = (f, { partners = [] } = {}) => ({
  yearEnd: f.yearEnd || null,
  // 'Other' stores the typed text — the same collapse an approval does.
  serviceProvided: f.service === 'Other'
    ? (blankToNull(f.serviceOther) || 'Other')
    : blankToNull(f.service),
  descriptionOfService: blankToNull(f.descriptionOfService),
  ultimateParent: blankToNull(f.ultimateParent),
  overseasOperations: strToTri(f.overseasOperations),
  consolidatedFs: blankToNull(f.consolidatedFs),
  isPie: strToTri(f.isPie),
  pieType: f.isPie === 'yes' ? blankToNull(f.pieType) : null,
  pieTypeOther: f.isPie === 'yes' && f.pieType === PIE_TYPE_OTHERS ? blankToNull(f.pieTypeOther) : null,
  proposedFees: String(f.proposedFees ?? '').trim() === '' ? null : Number(f.proposedFees),
  // 金額與幣別是一個邏輯欄位：金額空＝幣別 NULL，有金額沒選幣別＝SGD。
  proposedFeesCcy: String(f.proposedFees ?? '').trim() === '' ? null : (f.proposedFeesCcy || DEFAULT_CURRENCY),
  ssicPrimary: blankToNull(f.ssicPrimary),
  ssicSecondary: blankToNull(f.ssicSecondary),
  principalActivity1: blankToNull(f.principalActivity1),
  principalActivity2: blankToNull(f.principalActivity2),
  secRegistered: strToTri(f.secRegistered),
  isGroupAudit: strToTri(f.isGroupAudit),
  underInvestigation: strToTri(f.underInvestigation),
  tcwgFeeCommunicated: strToTri(f.tcwgFeeCommunicated),
  screeningRequired: strToTri(f.screeningRequired),
  partnerStaffIds: f.partnerStaffIds || [],
  directorStaffIds: f.directorStaffIds || [],
  managerStaffIds: f.managerStaffIds || [],
  teamStaffIds: f.teamStaffIds || [],
  remarks: blankToNull(f.remarks),
});

/**
 * Required-field check → the gaps as `[{ key, label }]` (the server re-validates).
 * `key` is the field's canonical key — the value the shared blocks below pass
 * to `anchor(...)` — so a page can map it through its own anchor and hand the
 * list to `formValidation` to mark the very controls that are empty. `label`
 * is the wording for the banner.
 *
 * `requireProfile` is Registration's full set. Management's New form asks for
 * it too (the three surfaces ask the same questions), but its
 * EDIT form does not — legacy engagements predate the client-profile fields
 * and have no answers, and blocking every edit behind eight unknowable fields
 * would make the page unusable.
 */
/** The sum of per-company amounts as a string (cent-exact); '' when none is entered. */
export const splitTotal = (split) => {
  const vals = Object.values(split || {}).filter((v) => String(v ?? '').trim() !== '').map(Number);
  if (vals.length === 0 || vals.some((n) => !Number.isFinite(n))) return '';
  return String(vals.reduce((t, n) => t + Math.round(n * 100), 0) / 100);
};

export const engagementRequiredGaps = (f, { requireProfile = true, multiFyeServices = [], companies = null } = {}) => {
  const gaps = [];
  const gap = (key, label) => gaps.push({ key, label });
  if (!f.yearEnd) gap('yearEnd', 'Financial Year End');
  // The shared answer of a group is checked unless the companies answer it
  // on their own — then every company's answer is (profileSplitGaps).
  const shared = (key) => !groupHasOwn(f, groupByKey(key));
  if (shared('service')) {
    if (!f.service) gap('serviceProvided', 'Service Provided');
    if (f.service === 'Other' && !String(f.serviceOther || '').trim()) gap('serviceOther', 'Other Service Description');
  }
  // Description of Service: only a taken slot makes it mandatory, and that is
  // the live duplicate-check's call (DESCRIPTION_REQUIRED blocks the submit).
  // People are not checked here: each surface applies its own rule
  // (Registration — the requester appears in a lead list; Management — at
  // least one of the three leads).
  // Every service line names its company (several companies) and a service.
  const lines = f.serviceLines || [];
  if ((f.entityIds || []).length > 1 && lines.some((l) => !l.entityId)) gap('serviceLines', 'Company of Every Additional Service');
  else if (lines.some((l) => !l.service || (l.service === 'Other' && !String(l.serviceOther || '').trim()))) gap('serviceLines', 'Service of Every Additional Service');
  if (!requireProfile) return [...gaps, ...profileSplitGaps(f, { companies })];
  if (shared('overseasOperations') && f.overseasOperations === '') gap('overseasOperations', 'Overseas Operations');
  if (shared('consolidatedFs') && f.consolidatedFs === '') gap('consolidatedFs', 'Consolidated Financial Statements');
  if (shared('pie')) {
    if (f.isPie === '') gap('isPie', PIE_LABEL);
    else if (f.isPie === 'yes' && !f.pieType) gap('pieType', PIE_TYPE_LABEL);
    else if (f.isPie === 'yes' && f.pieType === PIE_TYPE_OTHERS && !String(f.pieTypeOther || '').trim()) gap('pieType', PIE_TYPE_OTHER_LABEL);
  }
  // Estimated Fees: one engagement — the amount; two or more (companies and
  // service lines) — every engagement's own amount, the Total follows.
  const feeSplit = f.proposedFeesSplit && typeof f.proposedFeesSplit === 'object' ? f.proposedFeesSplit : {};
  const feeKeys = [...(f.entityIds || []), ...activeServiceLines(f).map((l) => lineFeeKey(l.k))];
  if (feeKeys.length > 1) {
    if (feeKeys.some((id) => String(feeSplit[id] ?? '').trim() === '')) gap('proposedFeesSplit', 'Estimated Fees for Every Engagement');
  } else if (String(f.proposedFees).trim() === '') gap('proposedFees', 'Estimated Fees');
  if (!String(f.ultimateParent || '').trim()) gap('ultimateParent', 'Ultimate Parent');
  // SSIC Code Secondary and Principal Activity 2 are optional: many clients
  // have a single line of business, so only the primary pair is required.
  if (shared('ssicPrimary') && !String(f.ssicPrimary || '').trim()) gap('ssicPrimary', 'SSIC Code Primary');
  if (shared('principalActivity1') && !String(f.principalActivity1 || '').trim()) gap('principalActivity1', 'Principal Activity 1');
  if (shared('secRegistered') && f.secRegistered === '') gap('secRegistered', 'SEC Registration');
  if (shared('isGroupAudit') && f.isGroupAudit === '') gap('isGroupAudit', 'Group Audit');
  if (shared('underInvestigation') && f.underInvestigation === '') gap('underInvestigation', 'Under Investigation');
  if (shared('tcwgFeeCommunicated') && f.tcwgFeeCommunicated === '') gap('tcwgFeeCommunicated', 'Fee Information Communicated to TCWG');
  gaps.push(...profileSplitGaps(f, { companies }));
  // The screening question is NOT checked here: it lives in
  // each surface's own Screening section and not every surface asks it, so the
  // surface that does asks for it (engRegRequiredGaps).
  return gaps;
};

/** The same check as a list of labels — for banners that only name the gaps. */
export const missingEngagementFields = (f, opts) => engagementRequiredGaps(f, opts).map((g) => g.label);

// ── The shared field blocks ────────────────────────────────────────────────
// `required` is off for the Amendment editor: an amendment changes what it
// wants to change, so a star on every field would be a lie.
//
// `anchor` opts a surface into the workflow return-comment layer
// (docs/workflow-ui.md §4.5): it maps a field's canonical key to the comment
// key that surface's detail page uses (Registration diffs people by NAME
// lists, Amendment prefixes the engagement id on multi-engagement requests).
// Surfaces without the comment layer (Management) omit it — `field=null` on a
// FormKit Field renders nothing, so they are byte-for-byte unaffected.
const noAnchor = () => null;

/**
 * The Date of Appointment as the client master records it — read-only on
 * every engagement surface. It is asked once, on New Client, and corrected
 * in Management → Clients & Entities only; an engagement never carries
 * its own copy (a legacy value written before the move is labelled as such).
 * `appointment` = { date, source } from the engagement read model.
 */
export const AppointmentDateNote = ({ appointment }) => {
  if (!appointment) return null;
  const { date, source } = appointment;
  return (
    <Field label="Date of Appointment" hint={date
      ? (source === 'engagement' ? 'Recorded on this engagement before the date moved to the client master.' : 'Set in the client master — change it in Management → Clients & Entities.')
      : 'Not recorded for this company — add it in Management → Clients & Entities.'}
    >
      <input type="text" value={date ? formatDate(date) : '—'} readOnly className={lockedInputCls} />
    </Field>
  );
};

/** The FYE — the one date every surface asks for. Date of Appointment is not
 *  asked here: it lives on the client master (AppointmentDateNote shows it
 *  read-only where one engagement is on screen). */
// No hint under the FYE — on every surface, not just Registration: this block
// is shared and the three New forms must not drift apart.
export const EngagementDateFields = ({ f, set, required = true, anchor = noAnchor, appointment = undefined }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
    <Field label="Financial Year End" required={required} field={anchor('yearEnd')}>
      <FyeField value={f.yearEnd} onChange={(v) => set('yearEnd', v)} />
    </Field>
    {appointment !== undefined && <AppointmentDateNote appointment={appointment} />}
  </div>
);

// ── Per-company answers (every form covering several companies: Registration,
// Management New, Screening request, Screening record) ──────────────────────
//
// Any client-profile question except Ultimate Parent may be answered per
// company (owner 2026-10-14): "Assign per entity" under the question swaps the
// control for one per ticked company, each starting from the shared answer;
// "Assign one answer to all entity" drops them. The form keeps them in `profileSplit`
// = { <form key>: { <entityId>: value } } (form spelling: 'yes' / 'no',
// `service` + `serviceOther`); groups that split together are the ones in
// src/utils/engagementProfile.js (service + other, PIE + type + Others text).
// A group is all or none: once one company answers it, every company must.
// Posted as `profileSplit` in the master spelling; the server keeps each
// company's own answers on its entity row (`profile`) and the request's
// answer becomes the first company's.

/** The form's companies as [{ id, name }], named from the options' entity list. */
export const companiesOf = (f, options) => (f.entityIds || []).map((id) => ({
  id, name: (((options || {}).entities || []).find((e) => e.id === id) || {}).entityName || `Company #${id}`,
}));

// Master spelling ↔ form spelling of the profile keys.
const FORM_KEY = { serviceProvided: 'service' };
const formKeyOf = (field) => FORM_KEY[field] || field;
const PROFILE_FORM_GROUPS = PROFILE_SPLIT_GROUPS.map((g) => ({ ...g, formKeys: g.fields.map(formKeyOf) }));
const groupByKey = (key) => PROFILE_FORM_GROUPS.find((g) => g.key === key);

const splitOfForm = (f) => (f.profileSplit && typeof f.profileSplit === 'object' ? f.profileSplit : {});
/** Does any company answer this group on its own (several companies only)? */
const groupHasOwn = (f, group) => (f.entityIds || []).length > 1
  && group.formKeys.some((k) => Object.keys(splitOfForm(f)[k] || {}).length > 0);

/** The form as ONE company answers it — its own answers laid over the shared ones. */
export const fieldsForCompany = (f, entityId) => {
  const split = splitOfForm(f);
  const own = {};
  Object.entries(split).forEach(([k, byId]) => {
    if (byId && byId[entityId] !== undefined && byId[entityId] !== null) own[k] = byId[entityId];
  });
  return { ...f, ...own };
};

/** The service lines whose company is ticked (a line keeps its row while its company is unticked, but counts for nothing). */
export const activeServiceLines = (f) => {
  const ids = new Set((f.entityIds || []).map(Number));
  return (f.serviceLines || []).filter((l) => l.entityId && ids.has(Number(l.entityId)));
};

/**
 * The slot question the live duplicate-check asks (company + FYE + service
 * [+ description]): while the service and the description are shared, every
 * ticked company is asked the form-wide question (`sharedIds`); once either is
 * answered per company, each company is asked with its own (`rows`, keyed
 * ownRowKey). Every service line is a row of its own (keyed lineRowKey), so
 * the answers come back under the right row.
 */
export const slotQuestionOf = (f) => {
  const ids = f.entityIds || [];
  const lines = activeServiceLines(f).map((l) => ({
    key: lineRowKey(l.k), entityId: l.entityId, service: l.service || '', serviceOther: l.serviceOther || '', description: l.descriptionOfService || '',
  }));
  const own = groupHasOwn(f, groupByKey('service')) || groupHasOwn(f, groupByKey('descriptionOfService'));
  if (!own) return { sharedIds: ids, rows: lines };
  return {
    sharedIds: [],
    rows: [
      ...ids.map((id) => {
        const c = fieldsForCompany(f, id);
        return { key: ownRowKey(id), entityId: id, service: c.service || '', serviceOther: c.serviceOther || '', description: c.descriptionOfService || '' };
      }),
      ...lines,
    ],
  };
};

/**
 * The slots the form itself fills twice (a company's own service against its
 * lines, line against line) — src/utils/serviceLines.repeatedSlotClashes,
 * keyed by lineRowKey. Blocks the submit like a live-check clash does.
 */
export const serviceLineClashes = (f, { companies = [], multiFyeServices = [] } = {}) => {
  if (activeServiceLines(f).length === 0) return [];
  const nameOf = (id) => ((companies || []).find((c) => c.id === id) || {}).name || `Company #${id}`;
  return repeatedSlotClashes([
    ...(f.entityIds || []).map((id) => {
      const c = fieldsForCompany(f, id);
      return { key: ownRowKey(id), entityId: id, entityName: nameOf(id), service: c.service, serviceOther: c.serviceOther, description: c.descriptionOfService };
    }),
    ...activeServiceLines(f).map((l) => ({
      key: lineRowKey(l.k), entityId: Number(l.entityId), entityName: nameOf(Number(l.entityId)),
      service: l.service, serviceOther: l.serviceOther, description: l.descriptionOfService,
    })),
  ], multiFyeServices);
};

/**
 * Is this group still unanswered in `values` (a form, or one company's merged
 * form)? The same rule for the shared answer and for every company.
 */
const groupGap = (group, v) => {
  switch (group.key) {
    case 'service': return !v.service || (v.service === 'Other' && !String(v.serviceOther || '').trim());
    case 'pie': return v.isPie === '' || v.isPie === undefined
      || (v.isPie === 'yes' && (!v.pieType || (v.pieType === PIE_TYPE_OTHERS && !String(v.pieTypeOther || '').trim())));
    case 'ssicPrimary': return !String(v.ssicPrimary || '').trim();
    case 'principalActivity1': return !String(v.principalActivity1 || '').trim();
    case 'overseasOperations': case 'consolidatedFs': case 'secRegistered':
    case 'isGroupAudit': case 'underInvestigation': case 'tcwgFeeCommunicated':
      return v[group.key] === '' || v[group.key] === undefined;
    default: return false; // description, the secondary pair: optional
  }
};

/** The per-company gaps: a split group every company must answer — `[{ key, label }]`. */
export const profileSplitGaps = (f, { companies = null, fields = null } = {}) => {
  const ids = companies ? companies.map((c) => c.id) : (f.entityIds || []);
  if (ids.length < 2) return [];
  return PROFILE_FORM_GROUPS
    .filter((g) => !fields || fields.includes(g.key))
    .filter((g) => groupHasOwn(f, g))
    .filter((g) => ids.some((id) => groupGap(g, fieldsForCompany(f, id))))
    .map((g) => ({ key: `${g.key}Split`, label: `${g.label} for Every Company` }));
};

const toPosted = (k, v) => {
  if (['isPie', 'overseasOperations', 'secRegistered', 'isGroupAudit', 'underInvestigation', 'tcwgFeeCommunicated'].includes(k)) {
    return v === 'yes' ? 'true' : v === 'no' ? 'false' : '';
  }
  return v === undefined || v === null ? '' : String(v);
};

/** The posted `profileSplit` (master spelling), ticked companies only; '{}' when nothing splits. */
export const profileSplitJson = (f) => {
  const covered = new Set((f.entityIds || []).map(String));
  const out = {};
  if (covered.size > 1) {
    PROFILE_FORM_GROUPS.filter((g) => groupHasOwn(f, g)).forEach((g) => {
      g.fields.forEach((field, i) => {
        const byId = splitOfForm(f)[g.formKeys[i]] || {};
        out[field] = Object.fromEntries([...covered].map((id) => [id, toPosted(field, byId[id])]));
      });
    });
  }
  return JSON.stringify(out);
};

const triToForm = (v) => (v === true ? 'yes' : v === false ? 'no' : '');
/** A saved request / record's per-company answers (entity rows' `profile`) as the form's `profileSplit`. */
export const profileSplitOf = (entities) => {
  const split = {};
  (entities || []).forEach((e) => {
    if (!e.entityId || !e.profile || typeof e.profile !== 'object') return;
    Object.entries(e.profile).forEach(([field, v]) => {
      const k = formKeyOf(field);
      const val = ['isPie', 'overseasOperations', 'secRegistered', 'isGroupAudit', 'underInvestigation', 'tcwgFeeCommunicated'].includes(field)
        ? triToForm(v) : (v === null || v === undefined ? '' : String(v));
      split[k] = { ...(split[k] || {}), [e.entityId]: val };
    });
  });
  return split;
};

/**
 * One group's per-company state: whether the form may split it (two or more
 * companies), whether it is split now, readers / writers for the shared
 * answer (no id) or one company's (id), and the two toggles.
 */
const usePerCompany = (f, set, group, companies) => {
  const split = splitOfForm(f);
  const splittable = (companies || []).length > 1;
  const hasOwn = splittable && group.formKeys.some((k) => Object.keys(split[k] || {}).length > 0);
  const [open, setOpen] = useState(hasOwn);
  useEffect(() => { if (hasOwn) setOpen(true); }, [hasOwn]);
  const get = (k, id) => (id === undefined ? f[k] : ((split[k] || {})[id] !== undefined ? split[k][id] : ''));
  const setv = (k, v, id) => {
    if (id === undefined) { set(k, v); return; }
    set('profileSplit', { ...split, [k]: { ...(split[k] || {}), [id]: v } });
  };
  // Every company starts from the shared answer — only the ones that differ need changing.
  const perCompany = () => {
    const next = { ...split };
    group.formKeys.forEach((k) => {
      next[k] = Object.fromEntries(companies.map((c) => [c.id, (split[k] || {})[c.id] !== undefined ? split[k][c.id] : (f[k] ?? '')]));
    });
    set('profileSplit', next);
    setOpen(true);
  };
  const useOne = () => {
    const next = { ...split };
    group.formKeys.forEach((k) => { delete next[k]; });
    set('profileSplit', next);
    setOpen(false);
  };
  return { splittable, open: splittable && open, get, setv, perCompany, useOne, setOpen };
};

// Several groups opened or closed together in ONE write of profileSplit
// (calling each group's perCompany / useOne in turn would have each one
// overwrite the other's keys from its own stale copy).
const openGroups = (f, set, groups, companies) => {
  const next = { ...splitOfForm(f) };
  groups.forEach((g) => g.formKeys.forEach((k) => {
    next[k] = Object.fromEntries(companies.map((c) => [c.id, (next[k] || {})[c.id] !== undefined ? next[k][c.id] : (f[k] ?? '')]));
  }));
  set('profileSplit', next);
};
const closeGroups = (f, set, groups) => {
  const next = { ...splitOfForm(f) };
  groups.forEach((g) => g.formKeys.forEach((k) => { delete next[k]; }));
  set('profileSplit', next);
};

// Short link; the full sentence pops up on hover / focus (owner 2026-10-16).
const PER_COMPANY_FULL = 'If the assessment differs across entities, click here to assign the appropriate assessment to each entity';
const PerCompanyToggle = ({ pc }) => {
  if (!pc.splittable) return null;
  if (pc.open) return <button type="button" onClick={pc.useOne} className={linkSplitToggle}>Assign one answer to all entity</button>;
  return (
    <span className="group relative inline-flex">
      <button type="button" onClick={pc.perCompany} className={linkSplitToggle}>
        Assessment differs across entities? Assign per entity
      </button>
      <span role="tooltip" className={`${tooltipPopupCls} absolute left-0 top-full mt-1.5 w-max max-w-[320px] opacity-0 invisible transition-opacity group-hover:opacity-100 group-hover:visible group-focus-within:opacity-100 group-focus-within:visible`}>
        {PER_COMPANY_FULL}
      </span>
    </span>
  );
};

/** One row per company — the company name, the control under it. */
const PerCompanyRows = ({ companies, render }) => (
  <div className={splitList}>
    {companies.map((c) => (
      <div key={c.id} className={splitListStackedRow}>
        <div className={splitListTitle}>{c.name}</div>
        {render(c)}
      </div>
    ))}
  </div>
);

/**
 * A profile question with "Assign per entity": `control(get, setv, id)` renders
 * the question's control(s) for the shared answer (id undefined) or for one
 * company. `fieldKey` is the shared control's anchor; the per-company rows
 * anchor on `<group>Split` (profileSplitGaps' key).
 */
const ProfileField = ({ f, set, group, label, hint, required = true, anchor = noAnchor, companies = [], fieldKey, control }) => {
  const pc = usePerCompany(f, set, group, companies);
  return (
    <Field label={label} hint={hint} required={required} field={anchor(fieldKey)}>
      {pc.open ? (
        <Field field={anchor(`${group.key}Split`)}>
          <PerCompanyRows companies={companies} render={(c) => control(pc.get, pc.setv, c.id)} />
        </Field>
      ) : control(pc.get, pc.setv, undefined)}
      <PerCompanyToggle pc={pc} />
    </Field>
  );
};

const ariaFor = (label, id, companies) => (id === undefined ? undefined
  : `${label} — ${((companies || []).find((c) => c.id === id) || {}).name || id}`);

let lineCounter = 0;
const newLineKey = () => `n${Date.now().toString(36)}${(lineCounter += 1)}`;

const descriptionCls = `${inputCls} block h-[41px] min-h-[41px] leading-[19px] resize-y`;

/**
 * Service Provided + Description of Service — one field, two shapes:
 *
 *   collapsed — one service select (+ "Other — describe") with the
 *     description beside it: every ticked company registers that service.
 *   the service list — one row per ticked company (its name, its service,
 *     its own description; always there, never removed), then the
 *     Additional Service rows added under them (`serviceLines`: a company
 *     picked on the row when several are ticked, a service, a description;
 *     removable, one more engagement each). "Add Another Service" appends
 *     one; "Assign one answer to all entity" (several companies) collapses
 *     the list and drops the lines.
 *
 * Opened by "Assign per entity" (the service and description groups split
 * together) or by adding a line; a single company opens only through its
 * lines. Each row is asked the live duplicate-check as its own slot
 * (`rowKey`: ownRowKey / lineRowKey) and checked against the form's other
 * rows (serviceLineClashes); what they say sits under the row.
 * `lines` = whether this surface offers Additional Service rows.
 */
const ServiceField = ({
  f, set, services = [], required, anchor, companies = [], descriptionNotice = null, descriptionNeeded = false,
  lines: linesEnabled = false, duplicates = [], notices = [], multiFyeServices = [],
}) => {
  const pcService = usePerCompany(f, set, groupByKey('service'), companies);
  const pcDesc = usePerCompany(f, set, groupByKey('descriptionOfService'), companies);
  const lines = linesEnabled ? (f.serviceLines || []) : [];
  const ids = (f.entityIds || []).map(Number);
  const several = companies.length > 1;
  const split = several && (pcService.open || pcDesc.open);
  const open = split || lines.length > 0;

  // Lines follow the ticked companies: one company ticked ⇒ every line is
  // its; a company unticked ⇒ its lines ask for a company again.
  const key = [...ids].sort((a, b) => a - b).join(',');
  useEffect(() => {
    if (!linesEnabled) return;
    const ticked = new Set(key ? key.split(',').map(Number) : []);
    set('serviceLines', (f.serviceLines || []).map((l) => {
      if (ticked.size === 1) return Number(l.entityId) === [...ticked][0] ? l : { ...l, entityId: [...ticked][0] };
      return l.entityId && !ticked.has(Number(l.entityId)) ? { ...l, entityId: '' } : l;
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, linesEnabled]);

  const feeSplitOf = () => (f.proposedFeesSplit && typeof f.proposedFeesSplit === 'object' ? f.proposedFeesSplit : {});
  const dropLineFees = (keys) => {
    const rest = { ...feeSplitOf() };
    let touched = false;
    keys.forEach((k) => { if (rest[lineFeeKey(k)] !== undefined) { delete rest[lineFeeKey(k)]; touched = true; } });
    if (!touched) return;
    set('proposedFeesSplit', rest);
    set('proposedFees', Object.keys(rest).length > 0 ? splitTotal(rest) : '');
  };
  const updateLine = (k, patch) => set('serviceLines', lines.map((l) => (l.k === k ? { ...l, ...patch } : l)));
  const removeLine = (k) => { set('serviceLines', lines.filter((l) => l.k !== k)); dropLineFees([k]); };
  const serviceGroups = [groupByKey('service'), groupByKey('descriptionOfService')];
  const expand = () => {
    openGroups(f, set, serviceGroups, companies);
    pcService.setOpen(true); pcDesc.setOpen(true);
  };
  const collapse = () => {
    closeGroups(f, set, serviceGroups);
    pcService.setOpen(false); pcDesc.setOpen(false);
    if (lines.length > 0) { dropLineFees(lines.map((l) => l.k)); set('serviceLines', []); }
  };
  const addLine = () => {
    if (several && !split) expand();
    set('serviceLines', [...lines, {
      k: newLineKey(), entityId: ids.length === 1 ? ids[0] : '', service: '', serviceOther: '', descriptionOfService: '',
    }]);
  };

  const formClashes = linesEnabled ? serviceLineClashes(f, { companies, multiFyeServices }) : [];
  // What the live check (and the form's own rows) say about one row.
  const rowNotice = (rowKey) => {
    const own = rowsFor(duplicates, rowKey);
    const ownNotices = rowsFor(notices, rowKey);
    const clash = formClashes.find((c) => c.key === rowKey) || null;
    return {
      notice: clash ? { tone: 'amber', text: clash.short || clash.message } : descriptionNoticeOf(own, ownNotices, { named: false }),
      needDesc: (clash && clash.code === 'DESCRIPTION_REQUIRED') || own.some((d) => d.code === 'DESCRIPTION_REQUIRED') || ownNotices.length > 0,
    };
  };

  const serviceSelect = (value, onChange, ariaLabel) => (
    <select value={value || ''} onChange={(e) => onChange(e.target.value)} className={inputCls} aria-label={ariaLabel}>
      <option value="">Select…</option>
      {services.map((sv) => <option key={sv} value={sv}>{sv}</option>)}
      <option value="Other">Other</option>
    </select>
  );
  const otherInput = (value, onChange, ariaLabel) => (
    <input type="text" value={value || ''} onChange={(e) => onChange(e.target.value)} className={inputCls}
      placeholder="Other service — describe" aria-label={ariaLabel} />
  );
  const descriptionInput = (value, onChange, ariaLabel, needDesc) => (
    <textarea rows="1" value={value || ''} onChange={(e) => onChange(e.target.value)} className={descriptionCls}
      placeholder={needDesc ? 'Description of Service (required)' : 'Description of Service'} aria-label={ariaLabel} />
  );

  if (!open) {
    const descTip = descriptionNeeded ? TAKEN_SLOT_DESCRIPTION_TIP : null;
    return (
      <>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Service Provided" required={required} field={anchor('serviceProvided')}>
            {serviceSelect(f.service, (v) => set('service', v))}
          </Field>
          <Field
            label={descTip ? <>Description of Service <InfoTip text={descTip} /></> : 'Description of Service'}
            required={descriptionNeeded} field={anchor('descriptionOfService')}
          >
            {descriptionInput(f.descriptionOfService, (v) => set('descriptionOfService', v), undefined, descriptionNeeded)}
          </Field>
          {f.service === 'Other' && (
            <Field label="Other Service — Describe" required={required} field={anchor('serviceOther')}>
              {otherInput(f.serviceOther, (v) => set('serviceOther', v))}
            </Field>
          )}
        </div>
        <div className="flex flex-wrap gap-x-4 -mt-2">
          {several && <PerCompanyToggle pc={{ ...pcService, perCompany: expand }} />}
          {linesEnabled && ids.length > 0 && (
            <button type="button" onClick={addLine} className={linkSplitToggle}>Add Another Service</button>
          )}
        </div>
        {descriptionNotice}
      </>
    );
  }

  // The service list: the companies' own rows, then the Additional Service rows.
  const own = (id) => {
    // One company: its row is the shared answer; several: its own answers.
    const get = (k) => (several ? pcService.get(k, id) : f[k]);
    const setv = (k, v) => (several ? (k === 'descriptionOfService' ? pcDesc.setv(k, v, id) : pcService.setv(k, v, id)) : set(k, v));
    return { get, setv };
  };
  return (
    <Field label="Service Provided" required={required} field={anchor('serviceProvided')}>
      <Field field={anchor(several ? 'serviceSplit' : 'descriptionOfService')}>
        <div className={splitList}>
          {companies.map((c) => {
            const { get, setv } = own(c.id);
            const { notice, needDesc } = several ? rowNotice(ownRowKey(c.id)) : { notice: null, needDesc: descriptionNeeded };
            return (
              <div key={c.id} className={splitListStackedRow}>
                <div className={splitListTitle}>{c.name}</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {serviceSelect(get('service'), (v) => setv('service', v), ariaFor('Service Provided', c.id, companies))}
                  {get('service') === 'Other' && otherInput(get('serviceOther'), (v) => setv('serviceOther', v), ariaFor('Other Service — Describe', c.id, companies))}
                  <div className={get('service') === 'Other' ? 'sm:col-span-2' : undefined}>
                    {descriptionInput(get('descriptionOfService'), (v) => setv('descriptionOfService', v), ariaFor('Description of Service', c.id, companies), needDesc)}
                  </div>
                </div>
                <NoticeLine notice={notice} />
              </div>
            );
          })}
          {lines.map((l, i) => {
            const { notice, needDesc } = rowNotice(lineRowKey(l.k));
            const label = `Additional Service ${i + 1}`;
            return (
              <div key={l.k} className={splitListExtraRow}>
                {/* The tag and Remove only: the company is the row's own
                    select (several companies) or the one ticked. */}
                <div className="flex items-center justify-between gap-2">
                  <span className={splitListExtraTag}>Additional</span>
                  <button type="button" onClick={() => removeLine(l.k)} className="text-xs text-slate-500 hover:text-slate-700 whitespace-nowrap">Remove</button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {several && (
                    <select value={l.entityId || ''} onChange={(e) => updateLine(l.k, { entityId: e.target.value ? Number(e.target.value) : '' })}
                      className={inputCls} aria-label="Additional service — company">
                      <option value="">Select the company…</option>
                      {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  )}
                  {serviceSelect(l.service, (v) => updateLine(l.k, { service: v, ...(v === 'Other' ? {} : { serviceOther: '' }) }), `Additional service — ${label}`)}
                  {l.service === 'Other' && otherInput(l.serviceOther, (v) => updateLine(l.k, { serviceOther: v }), `Other service — describe (${label})`)}
                  <div className={(several && l.service !== 'Other') || (!several && l.service === 'Other') ? 'sm:col-span-2' : undefined}>
                    {descriptionInput(l.descriptionOfService, (v) => updateLine(l.k, { descriptionOfService: v }), `Description of Service — ${label}`, needDesc)}
                  </div>
                </div>
                <NoticeLine notice={notice} />
              </div>
            );
          })}
        </div>
      </Field>
      <div className="flex flex-wrap gap-x-4">
        {linesEnabled && <button type="button" onClick={addLine} className={linkSplitToggle}>Add Another Service</button>}
        {several && <button type="button" onClick={collapse} className={linkSplitToggle}>Assign one answer to all entity</button>}
      </div>
      {!several && descriptionNotice}
    </Field>
  );
};

const PieTypeSelect = ({ value, onChange, ariaLabel }) => (
  <select value={value || ''} onChange={(e) => onChange(e.target.value)} className={inputCls} aria-label={ariaLabel}>
    <option value="">Select…</option>
    {PIE_TYPE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
  </select>
);

/**
 * "Public Interest Entity (PIE)?" + "Type of PIE" (+ its Others text) — the
 * same two questions the QC6 / QC7 form asks; the form starts from this
 * answer and its approval writes the answer back. Type of PIE appears only
 * with a Yes. `locked` (an approved QC6 / QC7 owns the answer): read-only.
 * Shared by the profile block, the Screening request and the Screening record.
 */
export const PieFields = ({ f, set, required = true, anchor = noAnchor, companies = [], locked = false }) => {
  const pc = usePerCompany(f, set, groupByKey('pie'), companies);
  if (locked) {
    return (
      <Field label={PIE_LABEL} field={anchor('isPie')} hint="Set by the approved QC6 / QC7 form — change it through QC Amendment.">
        <input type="text" readOnly className={lockedInputCls}
          value={pieText({ isPie: strToTri(f.isPie), pieType: f.pieType, pieTypeOther: f.pieTypeOther }) || '—'} />
      </Field>
    );
  }
  const typeControls = (get, setv, id) => (
    <div className="space-y-2">
      <PieTypeSelect value={get('pieType', id)} onChange={(v) => setv('pieType', v, id)} ariaLabel={ariaFor(PIE_TYPE_LABEL, id, companies)} />
      {get('pieType', id) === PIE_TYPE_OTHERS && (
        <input type="text" value={get('pieTypeOther', id) || ''} onChange={(e) => setv('pieTypeOther', e.target.value, id)}
          className={inputCls} placeholder={PIE_TYPE_OTHER_LABEL} aria-label={ariaFor(PIE_TYPE_OTHER_LABEL, id, companies)} />
      )}
    </div>
  );
  const yesNo = (get, setv, id) => (
    <YesNo value={get('isPie', id)} onChange={(v) => {
      setv('isPie', v, id);
      if (v !== 'yes') { setv('pieType', '', id); setv('pieTypeOther', '', id); }
    }} />
  );
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div className={pc.open ? 'sm:col-span-2' : undefined}>
      <Field label={PIE_LABEL} required={required} field={anchor('isPie')}>
        {pc.open ? (
          <Field field={anchor('pieSplit')}>
            <PerCompanyRows companies={companies} render={(c) => (
              <div className="space-y-2">
                {yesNo(pc.get, pc.setv, c.id)}
                {pc.get('isPie', c.id) === 'yes' && typeControls(pc.get, pc.setv, c.id)}
              </div>
            )} />
          </Field>
        ) : yesNo(pc.get, pc.setv, undefined)}
        <PerCompanyToggle pc={pc} />
      </Field>
      </div>
      {!pc.open && f.isPie === 'yes' && (
        <Field label={PIE_TYPE_LABEL} required={required} field={anchor('pieType')}>
          {typeControls(pc.get, pc.setv, undefined)}
        </Field>
      )}
    </div>
  );
};

/** Ultimate Parent — mandatory wherever the profile is asked (owner 2026-09-11); never per company. */
export const UltimateParentField = ({ f, set, required = true, anchor = noAnchor }) => (
  <Field label="Ultimate Parent" required={required} field={anchor('ultimateParent')}>
    <input type="text" value={f.ultimateParent} onChange={(e) => set('ultimateParent', e.target.value)} className={inputCls} />
  </Field>
);

const textControl = (key, label, companies) => (get, setv, id) => (
  <input type="text" value={get(key, id) || ''} onChange={(e) => setv(key, e.target.value, id)} className={inputCls} aria-label={ariaFor(label, id, companies)} />
);

/** SSIC codes + principal activities — the primary pair required, the secondary optional; each per company. */
export const SsicActivityFields = ({ f, set, required = true, anchor = noAnchor, companies = [] }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
    <ProfileField f={f} set={set} group={groupByKey('ssicPrimary')} label="SSIC Code Primary" required={required} anchor={anchor} companies={companies} fieldKey="ssicPrimary" control={textControl('ssicPrimary', 'SSIC Code Primary', companies)} />
    <ProfileField f={f} set={set} group={groupByKey('ssicSecondary')} label="SSIC Code Secondary" required={false} anchor={anchor} companies={companies} fieldKey="ssicSecondary" control={textControl('ssicSecondary', 'SSIC Code Secondary', companies)} />
    <ProfileField f={f} set={set} group={groupByKey('principalActivity1')} label="Principal Activity 1" required={required} anchor={anchor} companies={companies} fieldKey="principalActivity1" control={textControl('principalActivity1', 'Principal Activity 1', companies)} />
    <ProfileField f={f} set={set} group={groupByKey('principalActivity2')} label="Principal Activity 2" required={false} anchor={anchor} companies={companies} fieldKey="principalActivity2" control={textControl('principalActivity2', 'Principal Activity 2', companies)} />
  </div>
);

const yesNoControl = (key, choices) => (get, setv, id) => (
  <YesNo value={get(key, id)} onChange={(v) => setv(key, v, id)} choices={choices} />
);

/**
 * ② Service & Client Profile — identical on all three surfaces.
 *
 * `feesLocked`: once the engagement's QC6/QC7 acceptance is approved, the QC
 * figure (the form's "Estimated fees" question) is the
 * authoritative Estimated Fees — the field renders read-only and corrections
 * go through QC Amendment. Data-driven (the engagement has an approved QC),
 * not surface-specific: surfaces without an existing engagement (Registration)
 * simply never pass it. `pieLocked` (default: the same) does the same for the
 * PIE answer, which the approval writes back too.
 *
 * `companies` ([{ id, name }]): the ticked companies of a several-company
 * form — every question but Ultimate Parent and the fees offers "Set per
 * Company"; `feeCompanies` are the fee rows (companies, then service lines),
 * entered one by one whenever there are two or more (no Total-only).
 */
// One wording for the form and every read-only view of the answer.
export const TCWG_FEE_QUESTION_LABEL = "Transparency of Information Regarding Fees for Audit Clients that are Public Interest Entities: Have you communicated the fee-related information (for both audit and NAS) with those charged with governance (TCWG) to assist their assessment of the firm's independence prior to acceptance of the engagement?";

export const EngagementProfileFields = ({
  f, set, options = {}, required = true, anchor = noAnchor, feesLocked = false, pieLocked = feesLocked,
  descriptionNeeded = false, descriptionNotice = null, feeCompanies = [], companies = [],
  // The surfaces that create engagements offer service lines under the
  // companies; `duplicates` / `notices` are the live check's answers (the
  // lines pick theirs by rowKey).
  serviceLines = false, duplicates = [], notices = [],
}) => {
  // Two or more companies (Registration / Management New): one amount per
  // company — all of them required — under the currency picker, their sum as
  // the last row. One company: the amount itself.
  const splitFees = !feesLocked && (feeCompanies || []).length > 1;
  const split = f.proposedFeesSplit && typeof f.proposedFeesSplit === 'object' ? f.proposedFeesSplit : {};
  const setSplit = (id, v) => {
    const next = { ...split };
    if (String(v).trim() === '') delete next[id]; else next[id] = v;
    set('proposedFeesSplit', next);
    set('proposedFees', splitTotal(next));
  };
  const { services = [] } = options;
  const yn = (key, label, choices) => (
    <ProfileField f={f} set={set} group={groupByKey(key)} label={label} required={required} anchor={anchor} companies={companies} fieldKey={key} control={yesNoControl(key, choices)} />
  );
  return (
    <>
      {/* Service Provided with the Description of Service beside it (a taken
          slot — `descriptionNeeded`, from the live duplicate-check — makes the
          description mandatory and says why); what the check says sits right
          under the row it concerns. */}
      <ServiceField f={f} set={set} services={services} required={required} anchor={anchor} companies={companies}
        descriptionNotice={descriptionNotice} descriptionNeeded={descriptionNeeded}
        lines={serviceLines} duplicates={duplicates} notices={notices} multiFyeServices={options.multiFyeServices || []} />
      <PieFields f={f} set={set} required={required} anchor={anchor} companies={companies} locked={pieLocked} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field
          label="Estimated Fees"
          required={required && !feesLocked}
          field={anchor('proposedFees')}
          hint={feesLocked
            ? 'Set by the approved QC6 / QC7 form — change it through QC Amendment.'
            : (splitFees ? 'One amount per engagement; the Total adds them up.' : undefined)}
        >
          {/* inputCls 自帶 w-full——寬度用外層 div 控（幣別窄格、金額吃剩餘），
              不與 w-full 對撞。 */}
          {feesLocked ? (
            <div className="flex gap-2">
              <div className="w-24 shrink-0">
                <input type="text" value={f.proposedFeesCcy || DEFAULT_CURRENCY} readOnly className={lockedInputCls} />
              </div>
              <div className="flex-1 min-w-0">
                <input type="number" value={f.proposedFees} readOnly className={lockedInputCls} />
              </div>
            </div>
          ) : splitFees ? (
            <Field field={anchor('proposedFeesSplit')}>
              <div className="w-24 mb-2">
                <select
                  value={f.proposedFeesCcy || DEFAULT_CURRENCY}
                  onChange={(e) => set('proposedFeesCcy', e.target.value)}
                  className={inputCls}
                  aria-label="Estimated Fees currency"
                >
                  {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <SplitList
                rows={feeCompanies.map((c) => ({
                  key: c.id,
                  title: c.title || c.name,
                  subtitle: c.subtitle,
                  control: (
                    <input
                      type="number" min="0" step="0.01" placeholder="0.00"
                      value={split[c.id] === undefined ? '' : split[c.id]}
                      onChange={(e) => setSplit(c.id, e.target.value)}
                      aria-label={`Estimated Fees — ${c.name}`}
                      className={`${inputCls} text-right`}
                    />
                  ),
                }))}
                footer={(
                  <>
                    <span className="flex-1 text-sm font-semibold text-slate-700">Total</span>
                    <span className={`shrink-0 whitespace-nowrap pr-3 ${splitListTotal}`} aria-label="Estimated Fees — total">{f.proposedFeesCcy || DEFAULT_CURRENCY} {fmtFeeTotal(f.proposedFees)}</span>
                  </>
                )}
              />
            </Field>
          ) : (
            <div className="flex gap-2">
              <div className="w-24 shrink-0">
                <select
                  value={f.proposedFeesCcy || DEFAULT_CURRENCY}
                  onChange={(e) => set('proposedFeesCcy', e.target.value)}
                  className={inputCls}
                  aria-label="Estimated Fees currency"
                >
                  {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="flex-1 min-w-0">
                <input
                  type="number" min="0" step="0.01" value={f.proposedFees}
                  onChange={(e) => set('proposedFees', e.target.value)}
                  className={inputCls}
                />
              </div>
            </div>
          )}
        </Field>
      </div>
      <UltimateParentField f={f} set={set} required={required} anchor={anchor} />
      <SsicActivityFields f={f} set={set} required={required} anchor={anchor} companies={companies} />
      {yn('overseasOperations', 'Has overseas operations, or is a subsidiary of an overseas entity?')}
      {yn('consolidatedFs', 'Consolidated Financial Statements?', CONSOLIDATED_FS_OPTIONS)}
      {yn('secRegistered', 'If the client is registered with the SEC in the US?')}
      {yn('isGroupAudit', 'Is the engagement a Group audit?')}
      {yn('underInvestigation', 'Client under investigation by regulatory authorities?')}
      {yn('tcwgFeeCommunicated', TCWG_FEE_QUESTION_LABEL)}
    </>
  );
};

/**
 * "Does QM need to perform screening?" — deliberately NOT part of the profile
 * block above.
 *
 * The question and the document it governs belong together, so every surface
 * renders this field inside its own Screening section, immediately above the
 * BizFile one. Keeping it in the profile would split the two apart: the answer
 * in §2 and the upload five sections later.
 *
 * It is still ONE definition — the wording and the control live here, and each
 * surface only decides WHETHER it asks. Management → New engagement does not:
 * that form writes the engagement directly with no QM review
 * in between, so "does QM need to do it" has nobody to ask — it just files the
 * documents. Everything that goes through a reviewer (Registration, Amendment)
 * asks, and Management → Edit asks so the answer stays correctable.
 */
export const SCREENING_QUESTION_LABEL = 'Does QM need to perform screening?';
export const EngagementScreeningQuestion = ({ f, set, required = true, anchor = noAnchor }) => (
  <Field label={SCREENING_QUESTION_LABEL} required={required} field={anchor('screeningRequired')}>
    <YesNo value={f.screeningRequired} onChange={(v) => set('screeningRequired', v)} />
  </Field>
);

/** ③ People — Partner in charge (one), Directors / Managers / Team (many).
 *  The EQR is not asked here: the QC6 / QC7 acceptance assigns it.
 *
 *  The two workflow surfaces (Registration / Engagement Amendment) render the
 *  leads OPTIONAL (`required={false}`). Registration shows the team with the
 *  requester locked on it (`lockedTeamIds`; the server re-adds them anyway);
 *  Amendment HIDES the team (`showTeam={false}` — after creation the team
 *  changes through CaseWare, Charge Code & Team). Management keeps everything
 *  editable: it is the rescue entry and the place QM applies a completed team
 *  update. */
export const EngagementPeopleFields = ({
  f, set, options = {}, required = true, showTeam = true, lockedTeamIds = [], anchor = noAnchor,
}) => {
  const { partners = [], directors = [], managers = [], staff = [] } = options;
  const known = [...partners, ...directors, ...managers, ...staff, ...(f.knownStaff || [])];
  return (
    <>
      <Field label="Partner in Charge" required={required} field={anchor('partnerStaffIds')}>
        <SingleStaffPicker options={partners} selectedIds={f.partnerStaffIds} known={known}
          onChange={(v) => set('partnerStaffIds', v)} placeholder="Search partner…" />
      </Field>
      <Field label="Director(s)" required={required} field={anchor('directorStaffIds')}>
        <MultiStaffPicker options={directors} selectedIds={f.directorStaffIds} known={known}
          onChange={(v) => set('directorStaffIds', v)} placeholder="Add director…" />
      </Field>
      <Field label="Manager(s) in Charge" required={required} field={anchor('managerStaffIds')}>
        <MultiStaffPicker options={managers} selectedIds={f.managerStaffIds} known={known}
          onChange={(v) => set('managerStaffIds', v)} placeholder="Add manager…" />
      </Field>
      {showTeam && (
        <Field
          label="Team Member(s)"
          required={required}
          field={anchor('teamStaffIds')}
        >
          <MultiStaffPicker options={staff} selectedIds={f.teamStaffIds} known={known}
            onChange={(v) => set('teamStaffIds', v)} placeholder="Add team member…"
            lockedIds={lockedTeamIds} lockedNote="Requester" />
        </Field>
      )}
    </>
  );
};

/** Section hint for Management's engagement form (team fully editable there;
 *  at least one person across the leads and the team is required). */
export const PEOPLE_SECTION_HINT = (
  <>Fill in at least one of Partner in charge, Director(s), Manager(s) or Team Member(s). Directors / Managers / Team are multi-select.</>
);

/** Section hint for Registration — the requester is locked on the team. */
export const ENGREG_PEOPLE_HINT = (
  <>The requester is always a team member and cannot be removed. <InfoTip text="The person who registers the project stays a member of it, so every follow-up request (QC, CaseWare, Signed Financial Statements…) can still be filed. After approval, the team is changed through the CaseWare, Charge Code & Team Update flow." /></>
);

/** Section hint for Engagement Amendment — the team is not amendable here. */
export const EAR_PEOPLE_HINT = (
  <>Team members are changed through the CaseWare, Charge Code &amp; Team Update flow, not here.</>
);
